import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Settings, Save, Building, FormInput, ScanLine, GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

const settingSections = [
  { id: 'institute', label: 'Institute', icon: Building },
  { id: 'academic', label: 'Academic', icon: GraduationCap },
  { id: 'form', label: 'Form Builder', icon: FormInput },
  { id: 'ocr', label: 'OCR', icon: ScanLine },
];

const defaultForm = {
  instituteName: 'Paathshaala Institute',
  tagline: 'Smart Institute Management for Classes VI-XII',
  phone: '+91 98765 43210',
  email: 'info@paathshaala.local',
  address: '123 Education Lane, Knowledge City, India',
  sessionYear: '2025-26',
  receiptPrefix: 'PSR',
  registrationPrefix: 'PS',
  feeDueDay: 10,
  ocrProvider: 'ollama',
  confidenceThreshold: 0.7,
  autoCreate: false,
};

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('institute');
  const [formData, setFormData] = useState<Record<string, any>>({ ...defaultForm });
  const [synced, setSynced] = useState(false);

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await api.get<any>('/api/settings');
      return res.data;
    },
  });

  useEffect(() => {
    if (settingsData && !synced) {
      const loaded = typeof settingsData.data === 'object' ? settingsData.data : settingsData;
      setFormData(prev => ({ ...defaultForm, ...loaded }));
      setSynced(true);
    }
  }, [settingsData, synced]);

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => api.patch('/api/settings', payload),
    onSuccess: () => {
      toast.success('Settings saved successfully');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save settings');
    },
  });

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const handleChange = (key: string, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
            Settings
          </h1>
          <p className="text-xs sm:text-sm text-light-text-muted mt-1">Configure institute settings</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="h-10 gap-2"
        >
          {saveMutation.isPending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">Save Changes</span>
          <span className="sm:hidden">Save</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="glass-card rounded-2xl overflow-hidden">
            {settingSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 sm:px-4 py-3 text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400'
                    : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-elevated dark:hover:bg-dark-elevated'
                }`}
              >
                <section.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-4 sm:p-6 rounded-2xl"
          >
            {activeSection === 'institute' && (
              <div className="space-y-4">
                <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">Institute Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Institute Name</Label>
                    <Input
                      type="text"
                      value={formData.instituteName || ''}
                      onChange={(e) => handleChange('instituteName', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Tagline</Label>
                    <Input
                      type="text"
                      value={formData.tagline || ''}
                      onChange={(e) => handleChange('tagline', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Phone</Label>
                    <Input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Email</Label>
                    <Input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Address</Label>
                    <Textarea
                      className="w-full h-20 resize-none"
                      value={formData.address || ''}
                      onChange={(e) => handleChange('address', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'academic' && (
              <div className="space-y-4">
                <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">Academic Settings</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Session Year</Label>
                    <Input
                      type="text"
                      value={formData.sessionYear || ''}
                      onChange={(e) => handleChange('sessionYear', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Receipt Prefix</Label>
                    <Input
                      type="text"
                      value={formData.receiptPrefix || ''}
                      onChange={(e) => handleChange('receiptPrefix', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Registration Prefix</Label>
                    <Input
                      type="text"
                      value={formData.registrationPrefix || ''}
                      onChange={(e) => handleChange('registrationPrefix', e.target.value)}
                      className="h-10 w-full"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Fee Due Day</Label>
                    <Input
                      type="number"
                      value={formData.feeDueDay || ''}
                      onChange={(e) => handleChange('feeDueDay', parseInt(e.target.value) || 0)}
                      className="h-10 w-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'form' && (
              <div className="space-y-4">
                <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">Form Builder</h3>
                <p className="text-sm text-light-text-muted">Configure admission form fields</p>
                <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20">
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Go to <a href="/form-builder/admission" className="underline">Form Builder</a> to customize fields
                  </p>
                </div>
              </div>
            )}

            {activeSection === 'ocr' && (
              <div className="space-y-4">
                <h3 className="text-sm sm:text-lg font-semibold text-light-text dark:text-dark-text mb-4">OCR Settings</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">OCR Provider</Label>
                    <Select value={formData.ocrProvider || 'ollama'} onValueChange={(v) => handleChange('ocrProvider', v)}>
                      <SelectTrigger className="h-10 w-full">
                        <SelectValue placeholder="Select Provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ollama">Ollama Cloud (qwen3vl)</SelectItem>
                        <SelectItem value="huggingface">HuggingFace OCR</SelectItem>
                        <SelectItem value="mock">Mock (Development)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-light-text dark:text-dark-text mb-1">Confidence Threshold</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={formData.confidenceThreshold ?? 1}
                      onChange={(e) => handleChange('confidenceThreshold', parseFloat(e.target.value))}
                      className="h-10 w-full"
                    />
                  </div>
                  <div className="flex items-center gap-3 sm:col-span-2">
                    <input
                      type="checkbox"
                      id="autoCreate"
                      className="w-5 h-5 rounded border-purple-300"
                      checked={!!formData.autoCreate}
                      onChange={(e) => handleChange('autoCreate', e.target.checked)}
                    />
                    <Label htmlFor="autoCreate" className="text-sm text-light-text dark:text-dark-text">Auto-create student on high confidence</Label>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
